import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { TLoggable } from 'library/ts/utilities/logger/types/TLoggable';
import { isObject } from 'library/ts/utilities/modules/Type';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { IApiResponse } from '../interfaces/IApiResponse';
import { IMetadataItem } from '../interfaces/IMetadataItem';
import { PTVContentType } from '../model/enums/PTVContentType';

/**
 * @copyright © Paramount Streaming
 * @author Zeeshan Ahmad
 * @description Resolves Pluto TV stitcher paths from metadata items for DSP (Data Service Plugin) use.
 *
 * Stitcher URL segment convention (confirmed from stage stitcher testing and streaming-url.json API responses):
 * - Live channels: `channel` → `/stitch/hls/channel/{id}/master.m3u8`
 * - VOD (movies + episodes): `episode` → `/stitch/hls/episode/{id}/master.m3u8`
 *
 * Note: the Confluence Streaming URL API spec lists `vod` as the VOD segment — this is incorrect for stage.
 * Both stage stitcher testing and streaming-url.json API responses confirm `episode` is the correct segment.
 *
 * Synthesis (from content ID when `stitched.paths` absent) is handled at the `PlaybackParamsBuilderFeature`
 * level for VOD, via `synthesizePathFromId()`. This is the interim approach until Phase 2
 * (TN Web server-side `/v4.0/streaming-url.json` call per PLBCK-4361).
 */
export class PTVMetadataResolver {
	static readonly #EXTRACT_STITCHED_PATH = '#extractStitchedPath';
	static #logger: ILogger;
	static get logger() {
		if ( !PTVMetadataResolver.#logger ) {
			PTVMetadataResolver.#logger = LogService.create( { prefixes: [ 'PTVMetadataResolver' ] } );
		}

		return PTVMetadataResolver.#logger;
	}

	/**
	 * Infers playback format from stitched path extension.
	 * @param {string} path - Stitched path string (e.g. URL)
	 * @returns {'hls' | 'mpd' | undefined} - 'hls' if path contains .m3u8, 'mpd' if path contains .mpd, else undefined
	 * @private
	 */
	static #getFormatFromPath( path: string ): 'hls' | 'mpd' | undefined {
		if ( typeof path !== 'string' || !path ) {
			return undefined;
		}
		if ( path.includes( '.m3u8' ) ) {
			return 'hls';
		}
		if ( path.includes( '.mpd' ) ) {
			return 'mpd';
		}
		return undefined;
	}

	/**
	 * @description Resolves the stitcher path for a live channel from `STITCHER.channels`.
	 *
	 * Requires `STITCHER.channels` to contain an entry whose `id` matches `STITCHER.id`.
	 * If the matching channel has no `stitched.paths`, paths are synthesized using the
	 * `channel` URL segment: `/stitch/hls/channel/{id}/master.m3u8`.
	 *
	 * `accessTokenProvided` is false on the web path (no `accessToken`) — the streaming-url API
	 * only returns an MPD path when the session/device has established DRM support,
	 * so if MPD is present it should always be used (J Hausmann, Apr 20 2026).
	 * CTV always supplies `accessToken`, so `accessTokenProvided` is true there — format-based
	 * selection applies instead (CTV paths come from the metadata API, not streaming-url).
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {string | undefined}
	 */
	static resolveChannelStitchedPath( paramsVO: IParamsVO ): string | undefined {
		this.logger?.log( 'resolveChannelStitchedPath', 'paramsVO:', paramsVO );

		if ( !paramsVO?.STITCHER?.channels?.length ) {
			this.logger?.log( 'resolveChannelStitchedPath', 'no channels data' );
			return undefined;
		}

		this.logger?.log(
			'resolveChannelStitchedPath',
			'looking for channel with id:',
			paramsVO.STITCHER.id,
			'in channels:',
			paramsVO.STITCHER.channels as TLoggable
		);
		const channel = paramsVO.STITCHER.channels.find(
			( c ) => c.id === paramsVO.STITCHER.id
		);
		this.logger?.log( 'resolveChannelStitchedPath', 'found channel:', channel );

		return this.#extractStitchedPath( channel, paramsVO.STITCHER.format, 'channel', !!paramsVO.STITCHER.accessToken );
	}

	/**
	 * @description Resolves the stitcher path for a VOD movie from `STITCHER.items`.
	 *
	 * Returns `undefined` when `STITCHER.items` is absent — the caller (`PlaybackParamsBuilderFeature`)
	 * decides whether to fetch paths from the API (CTV, `accessToken` present) or synthesize
	 * via `synthesizePathFromId()` (TN Web, no `accessToken`).
	 *
	 * `accessTokenProvided` is false on the web path (no `accessToken`) — see `resolveChannelStitchedPath`
	 * for the full explanation of the web vs CTV split.
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {string | undefined}
	 */
	static resolveItemStitchedPath( paramsVO: IParamsVO ): string | undefined {
		this.logger?.log( 'resolveItemStitchedPath', 'paramsVO:', paramsVO );

		if ( !paramsVO?.STITCHER?.items ) {
			this.logger?.log( 'resolveItemStitchedPath', 'no items data' );
			return undefined;
		}

		this.logger?.log(
			'resolveItemStitchedPath',
			'looking for item with id:',
			paramsVO.STITCHER.id,
			'in items:',
			paramsVO.STITCHER.items as TLoggable
		);
		const item = paramsVO.STITCHER.items.find(
			( i ) => i._id === paramsVO.STITCHER.id
		);
		this.logger?.log( 'resolveItemStitchedPath', 'found item:', item );

		return this.#extractStitchedPath( item, paramsVO.STITCHER.format, 'episode', !!paramsVO.STITCHER.accessToken );
	}

	/**
	 * @description Resolves the stitcher path for a VOD episode from `STITCHER.episodes`.
	 *
	 * Returns `undefined` when `STITCHER.episodes` is absent — the caller (`PlaybackParamsBuilderFeature`)
	 * decides whether to fetch paths from the API (CTV, `accessToken` present) or synthesize
	 * via `synthesizePathFromId()` (TN Web, no `accessToken`).
	 *
	 * `accessTokenProvided` is false on the web path (no `accessToken`) — see `resolveChannelStitchedPath`
	 * for the full explanation of the web vs CTV split.
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {string | undefined}
	 */
	static resolveEpisodeStitchedPath( paramsVO: IParamsVO ): string | undefined {
		this.logger?.log( 'resolveEpisodeStitchedPath', 'paramsVO:', paramsVO );

		if ( !paramsVO?.STITCHER?.episodes ) {
			this.logger?.log( 'resolveEpisodeStitchedPath', 'no episodes data' );
			return undefined;
		}

		this.logger?.log(
			'resolveEpisodeStitchedPath',
			'looking for episode with id:',
			paramsVO.STITCHER.id,
			'in episodes:',
			paramsVO.STITCHER.episodes as TLoggable
		);
		const episode = paramsVO.STITCHER.episodes.find(
			( e ) => e._id === paramsVO.STITCHER.id
		);
		this.logger?.log( 'resolveEpisodeStitchedPath', 'found episode:', episode );

		return this.#extractStitchedPath( episode, paramsVO.STITCHER.format, 'episode', !!paramsVO.STITCHER.accessToken );
	}

	/**
	 * @description Resolves the stitcher path from a raw API response data array.
	 *
	 * Finds the matching item by `idField` and extracts or synthesizes the path.
	 * URL segment is determined by `STITCHER.type`: `PTVContentType.CHANNEL` → `'channel'`,
	 * all other types (movie, episode) → `'episode'`.
	 *
	 * @param {unknown} responseData - API response data object
	 * @param {IParamsVO} paramsVO
	 * @param {string} idField - Field name to match ID ('id', '_id', or 'slug')
	 * @returns {string | undefined}
	 */
	static resolveStitchedPathFromApiResponse(
		responseData: unknown,
		paramsVO: IParamsVO,
		idField: 'id' | '_id' | 'slug' = 'id'
	): string | undefined {
		this.logger?.log(
			'resolveStitchedPathFromApiResponse',
			'responseData:',
			responseData as TLoggable,
			'paramsVO:',
			paramsVO,
			'idField:',
			idField
		);

		const typedResponse = responseData as IApiResponse;
		if ( !typedResponse?.data ) {
			this.logger?.log( 'resolveStitchedPathFromApiResponse', 'no data', typedResponse as TLoggable );
			return undefined;
		}

		this.logger?.log(
			'resolveStitchedPathFromApiResponse',
			'looking for item with id:',
			paramsVO.STITCHER.id,
			'in data:',
			typedResponse.data as TLoggable,
			'idField:',
			idField
		);
		if ( !Array.isArray( typedResponse.data ) ) {
			return undefined;
		}
		const item = typedResponse.data.find(
			( i: IMetadataItem ) => i[ idField ] === paramsVO.STITCHER.id
		);
		this.logger?.log( 'resolveStitchedPathFromApiResponse', 'found item:', item );

		const urlSegment = paramsVO.STITCHER.type === PTVContentType.CHANNEL ? 'channel' : 'episode';
		return this.#extractStitchedPath( item, paramsVO.STITCHER.format, urlSegment );
	}

	/**
	 * @description Resolves a stitcher path from `window.stitcherPaths` set by TN Web after a
	 * server-side `/v4.0/streaming-url.json` call (PTVWS-3446 / PLBCK-4361 Phase 2).
	 *
	 * Format selection: MPD is always preferred when present. The streaming-url API only returns
	 * an MPD path when the session/device has established DRM support, so its presence is already
	 * the capability signal — no browser sniffing needed (J Hausmann, Apr 20 2026).
	 * HLS is used when MPD is absent (e.g. Safari, or content without a DASH stream).
	 *
	 * Returns `undefined` when `window.stitcherPaths` is absent or has no entry for the current
	 * content ID, allowing the caller to fall through to synthesis.
	 *
	 * @see https://paramount.atlassian.net/wiki/spaces/STC/pages/603947388/Streaming+URL+API+for+Convergence
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {string | undefined}
	 */
	static resolveStitchedPathFromWindow( paramsVO: IParamsVO ): string | undefined {
		const contentId = paramsVO?.STITCHER?.id;
		if ( !contentId || typeof window === 'undefined' ) {
			return undefined;
		}

		const paths = PTVMetadataResolver.#getWindowPaths( contentId );
		if ( !paths ) {
			this.logger?.log( 'resolveStitchedPathFromWindow', 'no window.stitcherPaths entry for contentId:', contentId );
			return undefined;
		}

		this.logger?.log( 'resolveStitchedPathFromWindow', 'found paths for contentId:', contentId, 'paths:', paths as TLoggable );

		// Prefer MPD — streaming-url API only returns it when DRM is confirmed for this session.
		const dashEntry = paths.find( ( p ) => p.path?.includes( '.mpd' ) );
		const hlsEntry = paths.find( ( p ) => p.path?.includes( '.m3u8' ) );
		const selected = dashEntry ?? hlsEntry;

		if ( !selected?.path ) {
			this.logger?.log( 'resolveStitchedPathFromWindow', 'no usable path in window.stitcherPaths for contentId:', contentId );
			return undefined;
		}

		const format = selected.path.includes( '.mpd' ) ? 'dash' : 'hls';
		this.logger?.log( 'resolveStitchedPathFromWindow', 'selected format:', format, 'path:', selected.path );

		// Path is returned as-is; PTVUrlBuilder.createAssetURL handles /v2 prefix normalization.
		return selected.path;
	}

	/**
	 * @description Reads `window.stitcherPaths[contentId]` set by TN Web after a server-side
	 * `/v4.0/streaming-url.json` call. Returns the paths array, or `undefined` when absent.
	 */
	static #getWindowPaths( contentId: string ): Array<{ type: string; path: string }> | undefined {
		const windowStitcherPaths = ( window as unknown as Record<string, unknown> )?.stitcherPaths;
		if ( !isObject( windowStitcherPaths ) ) {
			return undefined;
		}
		const paths = ( windowStitcherPaths as Record<string, Array<{ type: string; path: string }>> )[ contentId ];
		return Array.isArray( paths ) && paths.length ? paths : undefined;
	}

	/**
	 * @description Synthesizes a stitcher path directly from `STITCHER.id` when no pre-supplied
	 * metadata is available.
	 *
	 * Intended for use by `PlaybackParamsBuilderFeature` as the TN Web synthesis fallback —
	 * called when the resolver finds no existing data AND `accessToken` is absent (meaning
	 * the CTV API call path is not available).
	 *
	 * CTV app always generates and supplies `accessToken` — fall through to its API call flow.
	 * Web clients never supply `accessToken`; this method handles the synthesis fallback.
	 *
	 * Always synthesizes HLS regardless of `STITCHER.format`. Synthesis is only reached when
	 * `window.stitcherPaths` is absent (PTVWS-3446 / PLBCK-4361 Phase 2 not yet done), meaning
	 * DASH availability has not been confirmed for this content. Passing `format: "mpd"` from
	 * the site must not trigger DASH synthesis here — that would route through Shaka with a DRM
	 * config pointing at a prod concierge fallback, causing DRM init failures on preprod JWTs.
	 *
	 * @param {IParamsVO} paramsVO
	 * @param {'channel' | 'episode'} urlSegment - Stitcher path segment
	 * @returns {string | undefined}
	 */
	static synthesizePathFromId( paramsVO: IParamsVO, urlSegment: 'channel' | 'episode' ): string | undefined {
		const id = paramsVO?.STITCHER?.id;
		if ( !id ) {
			this.logger?.log( 'synthesizePathFromId', 'no STITCHER.id — cannot synthesize' );
			return undefined;
		}
		if ( paramsVO?.STITCHER?.format === 'mpd' ) {
			this.logger?.warn( 'synthesizePathFromId', 'STITCHER.format is "mpd" but window.stitcherPaths is absent — DASH availability unconfirmed; synthesizing HLS instead to avoid DRM failure. Complete PTVWS-3446 to enable DASH.' );
		}
		this.logger?.log( 'synthesizePathFromId', 'synthesizing from STITCHER.id:', id, 'segment:', urlSegment );
		return this.#extractStitchedPath( { _id: id, id }, 'hls', urlSegment );
	}

	/**
	 * @description Synthesizes stitched paths from content ID fields when `stitched.paths` is absent.
	 *
	 * HLS is always synthesized. MPD is additionally synthesized on CTV (`accessTokenProvided=true`)
	 * because CTV establishes DRM via `accessToken` rather than via the streaming-url API, and the
	 * CTV app is expected to pass `STITCHER.format` matching what the stitcher serves for that title.
	 * This is the temporary DASH-availability assumption made on CTV (PLBCK-6327) — preferred over
	 * the prior HLS-only regression.
	 *
	 * Web (`accessTokenProvided=false`) must remain HLS-only: synthesis runs when the streaming-url
	 * API hasn't been called yet, so DASH availability is unconfirmed. Including an MPD entry here
	 * would cause `#extractStitchedPath` (which always prefers MPD on the web path) to pick a phantom
	 * DASH path, routing Shaka to the prod DASH stitcher with a preprod JWT → 401/3005 error on
	 * live channels. On web, DASH paths are only selected when they come from a real streaming-url
	 * API response (populated in `stitched.paths` by TN Web via PTVWS-3446).
	 *
	 * URL segments — HLS shapes confirmed from stage stitcher testing and `streaming-url.json`
	 * responses; MPD shapes inferred from the same `streaming-url.json` response shape (HLS pattern
	 * with `dash`/`main.mpd` substituted) and not yet independently confirmed against the stitcher
	 * for the synthesized form:
	 * - `'channel'` (live):
	 *   - HLS: `/stitch/hls/channel/{id}/master.m3u8`
	 *   - MPD: `/stitch/dash/channel/{id}/main.mpd` (CTV-only, inferred)
	 * - `'episode'` (VOD — movies + episodes):
	 *   - HLS: `/stitch/hls/episode/{id}/master.m3u8`
	 *   - MPD: `/stitch/dash/episode/{id}/main.mpd` (CTV-only, inferred)
	 *
	 * Content ID is resolved using priority order: `videoContentId` → `contentId` → `content_id` → `_id` → `id`.
	 *
	 * @param item - Metadata item with content ID fields
	 * @param urlSegment - Stitcher URL path segment: `'channel'` for live, `'episode'` for VOD
	 * @param accessTokenProvided - When true (CTV), additionally synthesize an MPD path; when false (web), HLS only
	 * @returns Synthesized paths array, or undefined if no content ID found
	 */
	static #synthesizeStitchedPaths(
		item: IMetadataItem,
		urlSegment: 'channel' | 'episode',
		accessTokenProvided = false
	): Array<{ type: string; path: string }> | undefined {
		const contentId = [ item.videoContentId, item.contentId, item.content_id, item._id, item.id ]
			.find( ( v ) => v != null );
		if ( !contentId ) {
			return undefined;
		}
		// HLS is always synthesized. MPD is only synthesized on CTV (accessTokenProvided): CTV
		// establishes DRM via accessToken and passes STITCHER.format matching what's available.
		// On web (no accessToken), DASH availability is unconfirmed; a phantom MPD entry would be
		// picked by #extractStitchedPath (web path always prefers MPD), routing Shaka to the prod
		// DASH stitcher with a preprod JWT → 401/3005 error.
		const paths = [
			{ type: 'hls', path: `/stitch/hls/${urlSegment}/${contentId}/master.m3u8` }
		];
		if ( accessTokenProvided ) {
			const mpdPath = `/stitch/dash/${urlSegment}/${contentId}/main.mpd`;
			this.logger?.log( '#synthesizeStitchedPaths', 'accessTokenProvided — synthesizing MPD path:', mpdPath );
			paths.push( { type: 'mpd', path: mpdPath } );
		}
		return paths;
	}

	/**
	 * @description Ensures a metadata item has `stitched.paths` populated.
	 *
	 * If `stitched.path` or `stitched.paths` already exists, returns the item as-is.
	 * Otherwise delegates to `#synthesizeStitchedPaths` using the provided `urlSegment`.
	 *
	 * @param {IMetadataItem | undefined} item - Metadata item
	 * @param {('channel' | 'episode')} [urlSegment] - URL segment for path synthesis fallback
	 * @returns {IMetadataItem | undefined} The item with stitched paths populated, or undefined
	 */
	static #ensureStitchedPaths(
		item: IMetadataItem | undefined,
		urlSegment?: 'channel' | 'episode',
		accessTokenProvided = false
	): IMetadataItem | undefined {
		if ( item?.stitched?.path || item?.stitched?.paths?.length ) {
			return item;
		}
		if ( !urlSegment || !item ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'no stitched paths and no urlSegment for synthesis', item as TLoggable );
			return undefined;
		}
		const synthesized = this.#synthesizeStitchedPaths( item, urlSegment, accessTokenProvided );
		if ( !synthesized ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'no stitched paths and synthesis failed (no content ID)', item as TLoggable );
			return undefined;
		}
		this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'synthesized stitched paths from content ID:', synthesized as TLoggable );
		return { ...item, stitched: { ...item.stitched, paths: synthesized } };
	}

	/**
	 * Infers playback format from the available stitched paths when no explicit format is provided.
	 * @param {Array<{ type?: string; path?: string }>} paths - Available stitched paths
	 * @returns {string} Inferred format string ('hls' or 'mpd')
	 * @private
	 */
	static #inferFormat( paths: Array<{ type?: string; path?: string }> ): string {
		const hlsEntry = paths.find( ( p ) => p.type?.toLowerCase() === 'hls' );
		const mpdEntry = paths.find( ( p ) => p.type?.toLowerCase() === 'mpd' );
		const candidatePath = hlsEntry?.path || mpdEntry?.path;
		if ( candidatePath ) {
			const formatFromPath = this.#getFormatFromPath( candidatePath );
			if ( formatFromPath ) {
				return formatFromPath;
			}
		}
		return hlsEntry ? 'hls' : 'mpd';
	}

	/**
	 * @description Extracts a stitcher path string from a metadata item for the given format.
	 *
	 * When format is not provided and multiple paths exist, format is inferred from the
	 * path extension (`.m3u8` → `hls`, `.mpd` → `mpd`).
	 * Delegates to `#ensureStitchedPaths` to synthesize paths when absent.
	 *
	 * `accessTokenProvided` (false on web path, true on CTV) controls how multi-path entries are
	 * resolved:
	 * - Web path (`!accessTokenProvided`): if an MPD path is present it is always chosen, regardless
	 *   of the `format` hint passed by the site. The streaming-url API only returns an MPD path when
	 *   the session/device has already established DRM support — its presence is the capability
	 *   signal, no browser sniffing needed (J Hausmann, Apr 20 2026).
	 * - CTV path (`accessTokenProvided`): MPD is not auto-preferred; format-based selection applies
	 *   so the entry matching `STITCHER.format` (passed by the CTV app) is returned. CTV paths come
	 *   from the metadata API (not streaming-url) and `accessToken` is always present.
	 *
	 * @param {IMetadataItem | undefined} item - Metadata item
	 * @param {string | undefined} format - Desired format (`'hls'` or `'mpd'`); inferred from path when omitted
	 * @param {('channel' | 'episode')} [urlSegment] - URL segment for path synthesis fallback
	 * @param {boolean} accessTokenProvided - When false, MPD is always preferred over HLS when both are present (web path only)
	 * @returns {string | undefined}
	 */
	static #extractStitchedPath(
		item: IMetadataItem | undefined,
		format: string | undefined,
		urlSegment?: 'channel' | 'episode',
		accessTokenProvided = false
	): string | undefined {
		this.logger?.log(
			PTVMetadataResolver.#EXTRACT_STITCHED_PATH,
			'item:',
			item as TLoggable,
			'format:',
			format,
			'accessTokenProvided:',
			accessTokenProvided
		);

		const resolved = this.#ensureStitchedPaths( item, urlSegment, accessTokenProvided );
		if ( !resolved ) {
			return undefined;
		}

		if ( resolved.stitched?.path ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'found a single path (may not match format):', resolved.stitched.path );
			return resolved.stitched.path;
		}

		if ( resolved.stitched?.paths?.length === 1 ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'found a single path in stitched.paths (may not match format):', resolved.stitched.paths[ 0 ] );
			return resolved.stitched.paths[ 0 ].path;
		}

		// Web path (!accessTokenProvided): always use MPD when present — streaming-url API already confirmed DRM support.
		// CTV path (accessTokenProvided): fall through to format-based selection below.
		const dashEntry = resolved.stitched.paths.find( ( p ) => p.path?.includes( '.mpd' ) );
		const hlsEntry = resolved.stitched.paths.find( ( p ) => p.path?.includes( '.m3u8' ) );
		if ( !accessTokenProvided && dashEntry ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'no accessTokenProvided — using MPD path:', dashEntry as TLoggable );
			return dashEntry.path;
		}

		const effectiveFormat = format || this.#inferFormat( resolved.stitched.paths );
		if ( !format ) {
			this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'inferred format from path:', effectiveFormat );
		}

		this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'looking for path with type:', effectiveFormat, 'in paths:', resolved.stitched.paths as TLoggable );
		const pathEntry = resolved.stitched.paths.find(
			( p ) => p.type?.toLowerCase() === effectiveFormat.toLowerCase()
		);
		this.logger?.log( PTVMetadataResolver.#EXTRACT_STITCHED_PATH, 'found path:', pathEntry as TLoggable );
		return pathEntry?.path;
	}
}